import type Konva from 'konva';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Circle, Group, Rect as KonvaRect, Line, Text } from 'react-konva';
import { useShallow } from 'zustand/react/shallow';
import { useActiveClip } from '~/features/clips/selectors';
import { allBlobIndices } from '~/features/frames/pipeline';
import { discoverBlobs } from '~/features/pin/discover';
import { type ResizableEdge, resizeBBoxToEdge } from '~/features/pin/resizeBBox';
import { snapPoint } from '~/lib/snap';
import { useStore } from '~/store';
import { setStageCursor } from './cursors';
import { snapAxis, snapAxisDelta } from './snapAxis';

const PIVOT_RADIUS = 3;
const SNAP_RADIUS_STAGE_PX = 5;
const EDGE_HIT_STAGE_PX = 12;
const OVERLAY_COLOR = '#22d3ee';
const OVERLAY_SELECTED = '#fbbf24';
const OVERLAY_EDGE_ACTIVE = '#f43f5e';
const OVERLAY_HALO = 'rgba(15, 23, 42, 0.85)';
const OVERLAY_FILL = 'rgba(34, 211, 238, 0.16)';
const EMPTY_HINT_COLOR = 'rgba(148, 163, 184, 0.85)'; // slate-400 muted
const COUNT_HINT_COLOR = 'rgba(34, 211, 238, 0.9)'; // cyan-400, matches OVERLAY_COLOR
const COUNT_HINT_DURATION_MS = 1500;
const CONFIDENCE_LABEL_WIDTH = 36;
const CONFIDENCE_LABEL_HEIGHT = 14;

type BBox = { x: number; y: number; w: number; h: number };
type Point = { x: number; y: number };
type EdgeHandleRect = { x: number; y: number; width: number; height: number };
type EdgeHandleConfig = { side: ResizableEdge; cursor: 'ew-resize' | 'ns-resize' };

const EDGE_HANDLE_CONFIGS = [
  { side: 'left', cursor: 'ew-resize' },
  { side: 'right', cursor: 'ew-resize' },
  { side: 'top', cursor: 'ns-resize' },
  { side: 'bottom', cursor: 'ns-resize' },
] as const satisfies ReadonlyArray<EdgeHandleConfig>;

/** Snap candidates for a blob bbox: four corners, center, and bottom-center. */
function bboxSnapCandidates(bbox: BBox): Point[] {
  const { x, y, w, h } = bbox;
  return [
    { x, y },
    { x: x + w, y },
    { x, y: y + h },
    { x: x + w, y: y + h },
    { x: x + w / 2, y: y + h / 2 },
    { x: x + w / 2, y: y + h },
  ];
}

function edgeLinePoints(bbox: BBox, side: ResizableEdge): [number, number, number, number] {
  if (side === 'left') return [bbox.x, bbox.y, bbox.x, bbox.y + bbox.h];
  if (side === 'right') return [bbox.x + bbox.w, bbox.y, bbox.x + bbox.w, bbox.y + bbox.h];
  if (side === 'top') return [bbox.x, bbox.y, bbox.x + bbox.w, bbox.y];
  return [bbox.x, bbox.y + bbox.h, bbox.x + bbox.w, bbox.y + bbox.h];
}

function edgeHandleRect(bbox: BBox, side: ResizableEdge, hitSize: number): EdgeHandleRect {
  if (side === 'left') {
    return { x: bbox.x - hitSize / 2, y: bbox.y, width: hitSize, height: bbox.h };
  }
  if (side === 'right') {
    return { x: bbox.x + bbox.w - hitSize / 2, y: bbox.y, width: hitSize, height: bbox.h };
  }
  if (side === 'top') {
    return { x: bbox.x, y: bbox.y - hitSize / 2, width: bbox.w, height: hitSize };
  }
  return { x: bbox.x, y: bbox.y + bbox.h - hitSize / 2, width: bbox.w, height: hitSize };
}

function edgeValueFromNode(side: ResizableEdge, node: Konva.Rect, hitSize: number): number {
  if (side === 'left' || side === 'right') return node.x() + hitSize / 2;
  return node.y() + hitSize / 2;
}

/**
 * Pivot drag snap: bbox-corner / centroid snap takes precedence on each axis;
 * if an axis didn't snap to a bbox candidate, fall back to guideline snap on
 * that axis. Lets users align pivots to guidelines while keeping the existing
 * "stick to corner" behavior for in-bbox alignment.
 */
function snapPivot(raw: Point, bbox: BBox, stageScale: number): Point {
  const onBox = snapPoint(raw, bboxSnapCandidates(bbox), SNAP_RADIUS_STAGE_PX, stageScale);
  return {
    x: onBox.x !== raw.x ? onBox.x : snapAxis(raw.x, 'x'),
    y: onBox.y !== raw.y ? onBox.y : snapAxis(raw.y, 'y'),
  };
}

/**
 * Snaps a bbox-move drag to guidelines: any of the moved bbox's left/center/
 * right edges may snap to a vertical guideline (same for top/center/bottom →
 * horizontal). Mutates the dragged node so the visual stays aligned with the
 * snapped position. Returns the delta from the pre-drag origin.
 */
function snapBBoxMove(node: Konva.Node, origBbox: BBox): { dx: number; dy: number } {
  let nx = node.x();
  let ny = node.y();
  const dxCorr = snapAxisDelta([nx, nx + origBbox.w / 2, nx + origBbox.w], 'x');
  const dyCorr = snapAxisDelta([ny, ny + origBbox.h / 2, ny + origBbox.h], 'y');
  if (dxCorr !== 0) {
    nx += dxCorr;
    node.x(nx);
  }
  if (dyCorr !== 0) {
    ny += dyCorr;
    node.y(ny);
  }
  return { dx: nx - origBbox.x, dy: ny - origBbox.y };
}

function positionEdgeHandleNode(
  side: ResizableEdge,
  node: Konva.Rect,
  originalBBox: BBox,
  resizedBBox: BBox,
  hitSize: number,
): void {
  if (side === 'left') {
    node.x(resizedBBox.x - hitSize / 2);
    node.y(originalBBox.y);
    return;
  }
  if (side === 'right') {
    node.x(resizedBBox.x + resizedBBox.w - hitSize / 2);
    node.y(originalBBox.y);
    return;
  }
  if (side === 'top') {
    node.x(originalBBox.x);
    node.y(resizedBBox.y - hitSize / 2);
    return;
  }
  node.x(originalBBox.x);
  node.y(resizedBBox.y + resizedBBox.h - hitSize / 2);
}

type BlobHandleProps = {
  index: number;
  bbox: BBox;
  pivot: Point;
  stageScale: number;
  confidence: number | null;
  isSelected: boolean;
};

function BlobHandle({ index, bbox, pivot, stageScale, confidence, isSelected }: BlobHandleProps) {
  const setBlobOverride = useStore((s) => s.setBlobOverride);
  const setSelection = useStore((s) => s.setSelection);
  const setSelectedBlobs = useStore((s) => s.setSelectedBlobs);
  const toggleBlobSelection = useStore((s) => s.toggleBlobSelection);
  const selection = useStore((s) => s.editor.selectedEdge);

  // Capture pre-drag state at drag-start so move deltas are always relative to
  // the original position, not the mid-drag store state.
  const bboxDragRef = useRef<{
    origBbox: BBox;
    origPivotOverride: Point | undefined;
  } | null>(null);
  const edgeDragRef = useRef<{
    side: ResizableEdge;
    origBbox: BBox;
  } | null>(null);

  const selectedEdge =
    selection?.kind === 'pinEdge' && selection.index === index ? selection.side : null;
  const isPivotSelected = selection?.kind === 'pinPivot' && selection.index === index;
  const isBoxSelected = isSelected || selectedEdge !== null;

  // Box strokes use strokeScaleEnabled={false} — strokeWidth is already in
  // screen px, so do NOT divide by stageScale (that would shrink the line at
  // high zoom and inflate it at low zoom).
  const scaledEdgeHit = EDGE_HIT_STAGE_PX / stageScale;

  const applyEdgeDrag = (side: ResizableEdge, node: Konva.Rect) => {
    const drag = edgeDragRef.current;
    if (!drag) return;
    const rawEdge = edgeValueFromNode(side, node, scaledEdgeHit);
    const edge = snapAxis(rawEdge, side === 'left' || side === 'right' ? 'x' : 'y');
    const next = resizeBBoxToEdge(drag.origBbox, side, edge);
    positionEdgeHandleNode(side, node, drag.origBbox, next, scaledEdgeHit);
    setBlobOverride(index, { bbox: next });
  };

  return (
    <Group>
      <KonvaRect
        x={bbox.x}
        y={bbox.y}
        width={bbox.w}
        height={bbox.h}
        fill={OVERLAY_FILL}
        listening={false}
      />
      <KonvaRect
        x={bbox.x}
        y={bbox.y}
        width={bbox.w}
        height={bbox.h}
        stroke={OVERLAY_HALO}
        strokeWidth={isBoxSelected ? 5 : 4}
        strokeScaleEnabled={false}
        listening={false}
      />
      <KonvaRect
        x={bbox.x}
        y={bbox.y}
        width={bbox.w}
        height={bbox.h}
        stroke={isBoxSelected ? OVERLAY_SELECTED : OVERLAY_COLOR}
        strokeWidth={isBoxSelected ? 2.5 : 2}
        strokeScaleEnabled={false}
        listening={false}
      />

      {/* Transparent fill — drags the entire bbox; click selects in multi-select set. */}
      <KonvaRect
        x={bbox.x}
        y={bbox.y}
        width={bbox.w}
        height={bbox.h}
        fill="transparent"
        draggable
        onClick={(e) => {
          if (e.evt.shiftKey) toggleBlobSelection(index);
          else setSelectedBlobs([index]);
        }}
        onTap={() => setSelectedBlobs([index])}
        onMouseEnter={(e) => setStageCursor(e, 'move')}
        onMouseLeave={(e) => setStageCursor(e, '')}
        onDragStart={() => {
          setSelection({ kind: 'pinBlob', index });
          useStore.temporal.getState().pause();
          const { overrides } = useStore.getState().pin;
          bboxDragRef.current = {
            origBbox: { ...bbox },
            origPivotOverride: overrides[index]?.pivot,
          };
        }}
        onDragMove={(e) => {
          if (!bboxDragRef.current) return;
          const { origBbox, origPivotOverride } = bboxDragRef.current;
          const { dx, dy } = snapBBoxMove(e.target, origBbox);
          setBlobOverride(index, {
            bbox: {
              x: Math.round(origBbox.x + dx),
              y: Math.round(origBbox.y + dy),
              w: origBbox.w,
              h: origBbox.h,
            },
            // Live pivot follows un-rounded so mid-drag pixel jitter stays smooth.
            ...(origPivotOverride
              ? { pivot: { x: origPivotOverride.x + dx, y: origPivotOverride.y + dy } }
              : {}),
          });
        }}
        onDragEnd={(e) => {
          if (!bboxDragRef.current) return;
          const { origBbox, origPivotOverride } = bboxDragRef.current;
          const { dx, dy } = snapBBoxMove(e.target, origBbox);
          setBlobOverride(index, {
            bbox: {
              x: Math.round(origBbox.x + dx),
              y: Math.round(origBbox.y + dy),
              w: origBbox.w,
              h: origBbox.h,
            },
            ...(origPivotOverride
              ? {
                  pivot: {
                    x: Math.round(origPivotOverride.x + dx),
                    y: Math.round(origPivotOverride.y + dy),
                  },
                }
              : {}),
          });
          bboxDragRef.current = null;
          useStore.temporal.getState().resume();
        }}
      />

      {/* Selected edge highlight: replaces "outline" feedback with a distinct
       *  edge color so the user sees the box's edge itself change. */}
      {selectedEdge && (
        <Line
          points={edgeLinePoints(bbox, selectedEdge)}
          stroke={OVERLAY_EDGE_ACTIVE}
          strokeWidth={3.5}
          strokeScaleEnabled={false}
          listening={false}
        />
      )}

      {/* Selectable edge handles for precision resize (drag + keyboard nudge). */}
      {EDGE_HANDLE_CONFIGS.map(({ side, cursor }) => {
        const edgeRect = edgeHandleRect(bbox, side, scaledEdgeHit);
        return (
          <KonvaRect
            key={`${index}-${side}`}
            x={edgeRect.x}
            y={edgeRect.y}
            width={edgeRect.width}
            height={edgeRect.height}
            fill="transparent"
            draggable
            onMouseDown={() => setSelection({ kind: 'pinEdge', index, side })}
            onTap={() => setSelection({ kind: 'pinEdge', index, side })}
            onMouseEnter={(e) => setStageCursor(e, cursor)}
            onMouseLeave={(e) => setStageCursor(e, '')}
            onDragStart={() => {
              setSelection({ kind: 'pinEdge', index, side });
              useStore.temporal.getState().pause();
              edgeDragRef.current = {
                side,
                origBbox: { ...bbox },
              };
            }}
            onDragMove={(e) => applyEdgeDrag(side, e.target as Konva.Rect)}
            onDragEnd={(e) => {
              applyEdgeDrag(side, e.target as Konva.Rect);
              edgeDragRef.current = null;
              useStore.temporal.getState().resume();
            }}
          />
        );
      })}

      {/* Pivot dot — drags freely, snaps to bbox corners / center / bottom-center. */}
      <Circle
        x={pivot.x}
        y={pivot.y}
        radius={PIVOT_RADIUS / stageScale}
        fill={isPivotSelected ? OVERLAY_SELECTED : OVERLAY_COLOR}
        stroke={OVERLAY_HALO}
        strokeWidth={isPivotSelected ? 2 / stageScale : 1.5 / stageScale}
        onMouseDown={() => setSelection({ kind: 'pinPivot', index })}
        draggable
        onMouseEnter={(e) => setStageCursor(e, 'crosshair')}
        onMouseLeave={(e) => setStageCursor(e, '')}
        onDragStart={() => {
          setSelection({ kind: 'pinPivot', index });
          useStore.temporal.getState().pause();
        }}
        onDragMove={(e) => {
          const node = e.target as Konva.Circle;
          const snapped = snapPivot({ x: node.x(), y: node.y() }, bbox, stageScale);
          node.x(snapped.x);
          node.y(snapped.y);
        }}
        onDragEnd={(e) => {
          const node = e.target as Konva.Circle;
          const snapped = snapPivot({ x: node.x(), y: node.y() }, bbox, stageScale);
          setBlobOverride(index, { pivot: { x: Math.round(snapped.x), y: Math.round(snapped.y) } });
          node.x(snapped.x);
          node.y(snapped.y);
          useStore.temporal.getState().resume();
        }}
      />
      {confidence != null && (
        <Group listening={false}>
          <KonvaRect
            x={bbox.x + bbox.w - (CONFIDENCE_LABEL_WIDTH + 2) / stageScale}
            y={bbox.y + 2 / stageScale}
            width={CONFIDENCE_LABEL_WIDTH / stageScale}
            height={CONFIDENCE_LABEL_HEIGHT / stageScale}
            cornerRadius={3 / stageScale}
            fill={confidence >= 0.6 ? 'rgba(5, 150, 105, 0.95)' : 'rgba(220, 38, 38, 0.95)'}
            stroke={OVERLAY_HALO}
            strokeWidth={1 / stageScale}
          />
          <Text
            x={bbox.x + bbox.w - (CONFIDENCE_LABEL_WIDTH + 2) / stageScale}
            y={bbox.y + 4 / stageScale}
            width={CONFIDENCE_LABEL_WIDTH / stageScale}
            text={`${Math.round(confidence * 100)}%`}
            fontSize={10 / stageScale}
            fontStyle="bold"
            align="center"
            fill="#fff"
          />
        </Group>
      )}
    </Group>
  );
}

/**
 * Interactive overlay for pin/auto-detect framing mode.
 *
 * Also owns the synchronous blob-discovery effect: when framing mode is 'pin'
 * and blobs are null (cleared by source change or first entry), it calls
 * discoverBlobs synchronously using committedCleaned. The effect lives here
 * because it is the primary consumer of blobs and this co-location avoids
 * introducing a separate hook that would need re-render plumbing.
 *
 * The effect triggers on the same conditions as re-detection: entry into pin
 * mode, crop change, and tolerance/minArea control changes.
 *
 * stageScale is read from editor.stageScale (written by EditorStage on every
 * fit/zoom change) to avoid prop-threading through the ReactNode overlay slot.
 */
export function PinOverlay() {
  const stageScale = useStore((s) => s.editor.stageScale);
  const framingMode = useStore((s) => s.pin.framingMode);
  const blobs = useStore((s) => s.pin.blobs);
  const overrides = useStore((s) => s.pin.overrides);
  const order = useStore((s) => s.pin.order);
  const selectedBlobs = useStore(useShallow((s) => s.editor.selectedBlobs));
  const selectedBlobSet = useMemo(() => new Set(selectedBlobs), [selectedBlobs]);
  const detectTolerance = useStore((s) => s.pin.detectTolerance);
  const detectMinArea = useStore((s) => s.pin.detectMinArea);
  const setBlobs = useStore((s) => s.setBlobs);
  const activeClip = useActiveClip();
  const matches = useStore((s) => s.referencePoint.results);
  const isReferencePoint = activeClip?.alignmentMode === 'reference-point';
  const clipBlobs = activeClip?.source.kind === 'blobs' ? activeClip.source.blobs : null;

  const blobIndicesByFrameIndex = useMemo(() => {
    if (!blobs) return [];
    if (clipBlobs) return [...clipBlobs];
    return allBlobIndices(blobs, order);
  }, [blobs, order, clipBlobs]);

  const matchByBlobIndex = useMemo(() => {
    const confidenceByBlob = new Map<number, number>();
    if (!isReferencePoint || !matches) return confidenceByBlob;
    for (const match of matches) {
      const blobIndex = blobIndicesByFrameIndex[match.frameIndex];
      if (blobIndex === undefined) continue;
      confidenceByBlob.set(blobIndex, match.confidence);
    }
    return confidenceByBlob;
  }, [blobIndicesByFrameIndex, isReferencePoint, matches]);

  // Transient "N frames" hint that appears briefly when blob count changes.
  const [countHint, setCountHint] = useState<string | null>(null);
  const prevBlobCountRef = useRef<number | null>(null);
  useEffect(() => {
    if (!blobs) {
      prevBlobCountRef.current = null;
      return;
    }
    const prev = prevBlobCountRef.current;
    prevBlobCountRef.current = blobs.length;
    // Only show on a change from a prior known count (skip the very first render).
    if (prev === null || prev === blobs.length) return;
    const label = blobs.length === 1 ? '1 frame' : `${blobs.length} frames`;
    setCountHint(label);
    const id = setTimeout(() => setCountHint(null), COUNT_HINT_DURATION_MS);
    return () => clearTimeout(id);
  }, [blobs]);

  // committedCleaned is the ImageData snapshot from "Continue" in the matte step.
  const cleaned = useStore((s) => s.workflow.committedCleaned);
  // The workflow key changes when the source image changes — use it to reset blobs.
  const committedKey = useStore((s) => s.workflow.committedKey);
  const cropRect = useStore((s) => s.grid.cropRect);

  // Source image change → clear blobs so the effect below re-detects.
  useEffect(() => {
    // Dependency intentionally drives this effect.
    void committedKey;
    setBlobs(null);
  }, [committedKey, setBlobs]);

  // Crop change while in pin mode → clear blobs so the effect below re-detects.
  // Using a ref to skip the initial mount (we don't want to double-clear on entry).
  const prevCropRectRef = useRef(cropRect);
  useEffect(() => {
    if (prevCropRectRef.current === cropRect) return;
    prevCropRectRef.current = cropRect;
    if (framingMode !== 'pin') return;
    setBlobs(null);
  }, [cropRect, framingMode, setBlobs]);

  // Discover blobs synchronously when entering pin mode or when detection
  // parameters change while blobs are null.
  useEffect(() => {
    if (framingMode !== 'pin') return;
    if (!cleaned) return;
    if (blobs !== null) return;
    const result = discoverBlobs(cleaned, {
      alphaThreshold: detectTolerance,
      minArea: detectMinArea,
    });
    setBlobs(result);
  }, [framingMode, cleaned, blobs, detectTolerance, detectMinArea, setBlobs]);

  if (framingMode !== 'pin' || !blobs) return null;

  // Empty-state hint when detection ran but found nothing.
  if (blobs.length === 0) {
    return (
      <Text
        x={8}
        y={8}
        text="No frames detected — adjust matte tools (pick / region-key / eraser) and detection updates live."
        fontSize={12 / stageScale}
        fill={EMPTY_HINT_COLOR}
        listening={false}
        width={400 / stageScale}
        wrap="word"
      />
    );
  }

  return (
    <Group>
      {blobs.map((b, i) => {
        const bbox = overrides[i]?.bbox ?? b.bbox;
        const pivot = overrides[i]?.pivot ?? b.centroid;
        const confidence = matchByBlobIndex.get(i) ?? null;
        const blobKey = `${b.bbox.x}:${b.bbox.y}:${b.bbox.w}:${b.bbox.h}:${b.centroid.x}:${b.centroid.y}:${b.area}`;
        return (
          <BlobHandle
            key={blobKey}
            index={i}
            bbox={bbox}
            pivot={pivot}
            stageScale={stageScale}
            confidence={confidence}
            isSelected={selectedBlobSet.has(i)}
          />
        );
      })}
      {countHint && (
        <Text
          x={8}
          y={8}
          text={countHint}
          fontSize={12 / stageScale}
          fill={COUNT_HINT_COLOR}
          listening={false}
        />
      )}
    </Group>
  );
}
